import { useState } from "react";
import { UserAuth } from "../../../context/AuthContext";
import usePermissionsManager from "../hooks/usePermissionsManager";
import LoadingCircle from "../../../components/Global/LoadingCircle";
import AdminBlock from "./AdminBlock";
import strings from "../../../locales/ro.json";
import "./PermissionsManager.css";

const PermissionsTab = () => {
  const { user } = UserAuth();

  const {
    permissions,
    roles,
    rolePermissionsMap,
    loading,
    error,
    clearError,
    createPermission,
    deletePermission,
    toggleRolePermission,
  } = usePermissionsManager();

  const [newSlug, setNewSlug] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [savingCell, setSavingCell] = useState(null);

  const s = strings.admin.permissions;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSlug.trim() || !newLabel.trim()) return;
    setCreating(true);
    try {
      await createPermission({
        slug: newSlug.trim().toLowerCase(),
        label: newLabel.trim(),
        description: newDesc.trim() || null,
      });
      setNewSlug("");
      setNewLabel("");
      setNewDesc("");
    } catch (_) {}
    setCreating(false);
  };

  const handleDelete = async (perm) => {
    if (!window.confirm(s.list.deleteConfirm)) return;
    try {
      await deletePermission(perm.id);
    } catch (_) {}
  };

  const handleToggle = async (roleId, permId) => {
    const key = `${roleId}-${permId}`;
    setSavingCell(key);
    try {
      await toggleRolePermission(roleId, permId);
    } catch (_) {}
    setSavingCell(null);
  };

  if (loading && permissions.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <LoadingCircle />
      </div>
    );
  }

  return (
    <div className="pm-container">
      {error && (
        <div className="pm-error" onClick={clearError} style={{ cursor: "pointer" }}>
          {error} {s.tab.clickToDismiss}
        </div>
      )}

      {/* ─── Create Permission Form ─── */}
      <AdminBlock title={s.createForm.title} defaultOpen={false}>
        <form onSubmit={handleCreate} className="pm-create-form">
          <div className="pm-form-row">
            <div className="pm-field">
              <label>{s.createForm.slugLabel}</label>
              <input
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder={s.createForm.slugPlaceholder}
              />
            </div>
            <div className="pm-field">
              <label>{s.createForm.labelLabel}</label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={s.createForm.labelPlaceholder}
              />
            </div>
          </div>
          <div className="pm-field">
            <label>{s.createForm.descLabel}</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder={s.createForm.descPlaceholder}
            />
          </div>
          <button
            type="submit"
            className="pm-submit-btn"
            disabled={creating || !newSlug.trim() || !newLabel.trim()}
          >
            {creating ? s.createForm.submitLoading : s.createForm.submit}
          </button>
        </form>
      </AdminBlock>

      {/* ─── Permissions List ─── */}
      <AdminBlock title={s.list.title} defaultOpen={true}>
        {permissions.length === 0 ? (
          <p className="pm-empty">{s.list.empty}</p>
        ) : (
          <div className="pm-perm-list">
            {permissions.map((perm) => (
              <div key={perm.id} className="pm-perm-item">
                <div className="pm-perm-info">
                  <span className="pm-perm-slug">{perm.slug}</span>
                  <span className="pm-perm-label">{perm.label}</span>
                  {perm.description && (
                    <span className="pm-perm-desc">{perm.description}</span>
                  )}
                </div>
                <button
                  className="pm-delete-btn"
                  onClick={() => handleDelete(perm)}
                  title={s.list.deleteTitle}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </AdminBlock>

      {/* ─── Role × Permission Matrix ─── */}
      <AdminBlock title={s.matrix.title} defaultOpen={true}>
        {roles.length === 0 ? (
          <p className="pm-empty">{s.matrix.noRoles}</p>
        ) : permissions.length === 0 ? (
          <p className="pm-empty">{s.matrix.noPermissions}</p>
        ) : (
          <div className="pm-matrix-wrapper">
            <table className="pm-matrix">
              <thead>
                <tr>
                  <th className="pm-matrix-corner">{s.matrix.roleHeader}</th>
                  {permissions.map((perm) => (
                    <th key={perm.id} className="pm-matrix-perm-header">
                      <span className="pm-matrix-perm-label">{perm.label}</span>
                      <span className="pm-matrix-perm-slug">{perm.slug}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const groupedRoles = [];
                  let currentType = null;

                  roles.forEach((role) => {
                    const typeId = role.role_type?.id || 'unknown';
                    if (typeId !== currentType) {
                      groupedRoles.push({
                        isHeader: true,
                        label: role.role_type?.label || 'Other',
                        id: `header-${typeId}`
                      });
                      currentType = typeId;
                    }
                    groupedRoles.push(role);
                  });

                  return groupedRoles.map((item) => {
                    if (item.isHeader) {
                      return (
                        <tr key={item.id} className="pm-matrix-type-header">
                          <td className="pm-matrix-type-label">
                            {item.label}
                          </td>
                          {permissions.map((perm) => (
                            <td key={perm.id} className="pm-matrix-type-spacer" />
                          ))}
                        </tr>
                      );
                    }

                    const role = item;
                    return (
                      <tr key={role.id}>
                        <td className="pm-matrix-role-cell">
                          <span
                            className="pm-role-badge"
                            style={{ backgroundColor: role.color }}
                          >
                            {role.name}
                          </span>
                        </td>
                        {permissions.map((perm) => {
                          const isChecked = (rolePermissionsMap[role.id] || []).includes(
                            perm.id
                          );
                          const cellKey = `${role.id}-${perm.id}`;
                          const isSaving = savingCell === cellKey;

                          return (
                            <td key={perm.id} className="pm-matrix-cell">
                              <label className={`pm-checkbox-wrapper ${isSaving ? "saving" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleToggle(role.id, perm.id)}
                                  disabled={isSaving}
                                />
                                <span className="pm-checkmark"></span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </AdminBlock>
    </div>
  );
};

export default PermissionsTab;
